import { useEffect, useState } from 'react';
import { AlertCircle, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'src/components/ui/alert-dialog';
import { Button } from 'src/components/ui/button';
import { useSelector } from 'src/redux/store';
import { cn } from 'src/utils/utils';
import { useSupportChat } from '../chat/SupportChatContext';

// Define subscription status types based on Stripe's actual statuses
type SubscriptionStatus =
  | 'active'
  | 'past_due'
  | 'unpaid'
  | 'canceled'
  | 'incomplete'
  | 'incomplete_expired'
  | 'trialing'
  | 'paused';

// Sample subscription data with different statuses

// Define which statuses require payment resolution
const statusesRequiringAction: SubscriptionStatus[] = [
  'past_due',
  'unpaid',
  'incomplete',
  'incomplete_expired',
];

// Simple status descriptions for the modal
const statusDescriptions: Record<SubscriptionStatus, string> = {
  past_due: 'Payment failed but subscription still active',
  unpaid: 'Subscription deactivated due to failed payments',
  incomplete: 'Initial payment failed, subscription not activated',
  incomplete_expired: 'Initial payment failed and setup period expired',
  active: 'Active and in good standing',
  trialing: 'In trial period',
  paused: 'Temporarily paused',
  canceled: 'Canceled',
};

export default function PaymentAlert() {
  const { toggleOpen } = useSupportChat();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const subs = useSelector(
    (state) => state.stripe.subscriptionMap && Object.values(state.stripe.subscriptionMap)
  );

  const formattedSubscriptions = subs.map((sub) => ({
    id: sub.id,
    status: sub.status,
    productName: sub.items.map((item) => item.price.product.name).join(', '),
    // currentPeriodEnd: new Date(sub.currentPeriodEnd).toLocaleDateString(),
  }));

  // Filter subscriptions to only those requiring payment resolution
  const subscriptionsWithIssues = formattedSubscriptions.filter((sub) =>
    statusesRequiringAction.includes(sub.status)
  );

  const showAlert = subscriptionsWithIssues.length > 0 && !isDismissed;

  // Animation effect when component mounts
  useEffect(() => {
    if (showAlert) {
      // Small delay to allow for animation
      const timer = setTimeout(() => {
        setIsAlertVisible(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showAlert]);

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsAlertVisible(false);
    // Allow animation to complete before fully removing
    setTimeout(() => {
      setIsDismissed(true);
    }, 300);
  };

  console.log({ subs });

  // If there are no payment issues, don't display any alerts
  if (subscriptionsWithIssues.length < 1) {
    return null;
  }

  const issueCount = subscriptionsWithIssues.length;
  const multipleIssues = issueCount > 1;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Single alert for all payment issues */}
      {showAlert && (
        <div
          className={cn(
            'fixed bottom-4 right-4 max-w-sm w-full z-50 transition-all duration-300 ease-in-out',
            isAlertVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
          )}
        >
          <div
            role="button"
            onClick={() => setIsDialogOpen(true)}
            className="bg-destructive text-destructive-foreground rounded-lg shadow-lg p-4 pr-10 cursor-pointer relative"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium mb-1">
                  Payment Issue{multipleIssues ? 's' : ''} Detected
                </h3>
                <p className="text-sm opacity-90">
                  {multipleIssues
                    ? `We've detected issues with ${issueCount} of your subscriptions. Click to resolve.`
                    : `We've detected an issue with your ${subscriptionsWithIssues[0].productName} subscription. Click to resolve.`}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleDismiss}
              className="absolute top-3 right-3 text-destructive-foreground/70 hover:text-destructive-foreground"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Simplified modal that lists all issues and directs to support */}
      <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Subscription Payment Issue{multipleIssues ? 's' : ''}
            </AlertDialogTitle>
            <AlertDialogDescription>
              <p className="mb-4">
                We've detected {multipleIssues ? 'some issues' : 'an issue'} with your subscription
                {multipleIssues ? 's' : ''}. Please contact our support team for assistance.
              </p>

              <div className="border rounded-md p-4 mb-4 bg-muted/50">
                <h3 className="font-medium mb-2">
                  Affected Subscription{multipleIssues ? 's' : ''}
                </h3>
                <ul className="space-y-3">
                  {subscriptionsWithIssues.map((sub) => (
                    <li key={sub.id} className="flex flex-col">
                      <span className="font-medium">{sub.productName}</span>
                      <span className="text-sm text-muted-foreground">
                        Status: {statusDescriptions[sub.status]}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-sm text-muted-foreground">
                Our support team can help resolve these issues and ensure your service continues
                uninterrupted.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Dismiss
            </Button>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={toggleOpen}>
              Contact Support
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
